/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, RefCallback, RefObject } from 'preact';
import { forwardRef } from 'preact/compat';
import { borderInterpolations } from '../utils/UNSAFE_interpolations/borders';
import type { BorderProps } from '../utils/UNSAFE_interpolations/borders';
import { dimensionInterpolations } from '../utils/UNSAFE_interpolations/dimensions';
import type { DimensionProps } from '../utils/UNSAFE_interpolations/dimensions';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { colorInterpolations } from '../utils/UNSAFE_interpolations/colors';
import type { ColorProps } from '../utils/UNSAFE_interpolations/colors';
import { paddingInterpolations } from '../utils/UNSAFE_interpolations/padding';
import type { PaddingProps } from '../utils/UNSAFE_interpolations/padding';
import { ariaInterpolations } from '../utils/UNSAFE_interpolations/aria';
import type { AriaProps } from '../utils/UNSAFE_interpolations/aria';
import { themeInterpolations } from '../utils/UNSAFE_interpolations/theme';
import type { ThemeProps } from '../utils/UNSAFE_interpolations/theme';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import type { Role } from '../utils/UNSAFE_roles';
import { EnvironmentProvider } from '..//UNSAFE_Environment';
import type { ThemeModifiers } from '../UNSAFE_Theme';
import { isEmpty } from 'ramda';
import { globalVars } from '../Common/themes/themeContract.css';
import { assignInlineVars } from '@vanilla-extract/dynamic';

const elementTypes = ['div', 'main', 'article', 'section', 'aside'] as const;

type ElementType = (typeof elementTypes)[number];

type StyleProps = BorderProps &
  DimensionProps &
  FlexitemProps &
  ColorProps &
  PaddingProps &
  AriaProps &
  ThemeProps;

type Props = StyleProps & {
  as?: ElementType;
  role?: Role;
  children?: ComponentChildren;
};

const interpolations = [
  ...Object.values(borderInterpolations),
  ...Object.values(dimensionInterpolations),
  ...Object.values(flexitemInterpolations),
  ...Object.values(colorInterpolations),
  ...Object.values(paddingInterpolations),
  ...Object.values(ariaInterpolations),
  ...Object.values(themeInterpolations)
];

const mergedInterpolations = mergeInterpolations<Props>(interpolations);

type RefType = (RefObject<HTMLDivElement> | RefCallback<HTMLDivElement>) &
  (RefObject<HTMLElement> | RefCallback<HTMLElement>);

const View = forwardRef<RefType, Props>(
  ({ as, children, colorScheme, scale, color, backgroundColor, ...rest }, ref) => {
    const {
      class: className,
      ariaLabelledBy,
      ariaLabel,
      ...styles
    } = mergedInterpolations({
      colorScheme,
      scale,
      backgroundColor,
      color,
      ...rest
    });
    // We need to update the environment with any theme related props specified
    const env: Partial<ThemeModifiers> = {
      ...(colorScheme && { colorScheme }),
      ...(scale && { scale }),
      ...(backgroundColor && { currentBgColor: backgroundColor })
    };
    const props = {
      ['aria-label']: ariaLabel,
      ['aria-labelledby']: ariaLabelledBy,
      children,
      className,
      ref: ref as RefType,
      style: {
        ...styles,
        ...(backgroundColor &&
          assignInlineVars({
            [globalVars.currentBackgroundColor]: styles['backgroundColor']
          })),
        // we hardcode box-sizing: border-box on all of our Preact components.
        boxSizing: 'border-box'
      }
    };

    const Component = as || 'div';
    return isEmpty(env) ? (
      <Component {...props} />
    ) : (
      <EnvironmentProvider environment={env}>
        <Component {...props} />
      </EnvironmentProvider>
    );
  }
);

export { elementTypes, View };
